import React from "react";
import "../ComparisonChartStatic/ComparisonChartStatic.scss";

import requirementsPositive from "../../assets/icons/mdp-requirements-yes.svg";
import requirementsNegative from "../../assets/icons/mdp-requirements-no.svg";

function CheckIcon({ check }) {
  return (
    <div className="as-requirement-container">
      <img
        style={{ width: "15px", height: "15px" }}
        src={check ? requirementsPositive : requirementsNegative}
        alt=""
      />
    </div>
  );
}

function NotAvailableIcon() {
  return <div className="as-requirement-container">-</div>;
}

export default function TrimComparisonChart({ trims }) {
  // Sort trims by price (low to high) FIRST
  const sortedTrims = [...trims].sort(
    (a, b) => a.raw_baseMSRP - b.raw_baseMSRP
  );

  function formatPrice(value) {
    return new Intl.NumberFormat("en-US").format(value);
  }

  // Feature display mapping with friendly names
  const featureDisplayNames = {
    airConditioning: "Air Conditioning",
    heatedSeats: "Heated Front Seats",
    coolingSeats: "Ventilated Front Seats",
    heatedRearSeats: "Heated Rear Seats",
    heatedSteeringWheel: "Heated Steering Wheel",
    powerSeats: "Power Adjustable Seats",
    seatsType: "Seat Material",
    memorySettings: "Memory Settings",
    seatMemory: "Seat Memory",
    seatingCapacity: "Seating Capacity",
    adaptiveCruiseControl: "Adaptive Cruise Control",
    laneDepartureWarning: "Lane Departure Warning",
    blindspotWarning: "Blind Spot Warning",
    forwardCollisionMitigation: "Forward Collision Mitigation",
    rearCollisionAlert: "Rear Cross Traffic Alert",
    autoHighBeamHeadlights: "Auto High Beam Headlights",
    hillStartAssist: "Hill Start Assist",
    automaticBrakeHold: "Automatic Brake Hold",
    childDoorLock: "Child Door Lock",
    bluetooth: "Bluetooth",
    navigationSystem: "Navigation System",
    appleCarPlayAndroidAuto: "Apple CarPlay & Android Auto",
    premiumSoundSystem: "Premium Sound System",
    steeringWheelMountedAudioControls: "Steering Wheel Audio Controls",
    wirelessPhoneCharging: "Wireless Phone Charging",
    usbPorts: "USB Ports",
    hotspotAccess: "Wi-Fi Hotspot",
    keylessEntryAndPushStart: "Keyless Entry & Push Start",
    remoteStart: "Remote Start",
    powerLiftgate: "Power Liftgate",
    firstRowSunroof: "Sunroof/Moonroof",
    powerWindowsFirstRow: "Power Windows",
    powerDoorMirrors: "Power Door Mirrors",
    heatedDoorMirrors: "Heated Door Mirrors",
    tailgateLockUnlock: "Power Tailgate Lock",
    rearCamera: "Rearview Camera",
    exteriorParkingCameraFront: "Front Parking Camera",
    exteriorParkingCameraLeft: "Left Parking Camera",
    exteriorParkingCameraRight: "Right Parking Camera",
    aerialViewCamera: "360° Camera",
    fontAndRearParkingSensors: "Parking Sensors",
    wheels: "Wheels",
    roofRails: "Roof Rails",
    roofRack: "Roof Rack",
    runningBoards: "Running Boards",
    trailerHitch: "Trailer Hitch",
    trailerBrakeController: "Trailer Brake Controller",
    engineLitres: "Engine Size (L)",
    horsepower: "Horsepower",
    torqueLbFt: "Torque (lb-ft)",
    transmission: "Transmission",
    driveType: "Drive Type",
    stopAndGo: "Stop & Go",
    powertrain: "Powertrain Type",
    regenerativeBreaks: "Regenerative Braking",
    fuelConsumptionCityMPG: "City MPG",
    fuelConsumptionHighwayMPG: "Highway MPG",
    fuelConsumptionCombinedMPG: "Combined MPG",
    fuelTankCapacityGallons: "Fuel Tank (gal)",
    towingCapacityLbs: "Towing Capacity (lbs)",
    overallLengthInches: "Overall Length (in)",
    wheelbaseInches: "Wheelbase (in)",
    groundClearanceInches: "Ground Clearance (in)",
    numberOfDoors: "Number of Doors",
    frontRowLegRoomInches: "Front Legroom (in)",
    rearLegRoomInches: "2nd Row Legroom (in)",
    thirdRowLegRoomInches: "3rd Row Legroom (in)",
    frontHeadRoomInches: "Front Headroom (in)",
    rearHeadRoomInches: "2nd Row Headroom (in)",
    thirdRowHeadRoomInches: "3rd Row Headroom (in)",
    interiorRearCargoVolumeCubicFeet: "Cargo Volume (cu ft)",
    interiorRearCargoVolumeWithSeatsFoldedCubicFeet:
      "Cargo Volume (Seats Folded)",
    maxInteriorRearCargoVolumeCubicFeet: "Max Cargo Volume (cu ft)",
    powerOutlet12VDC: "12V Power Outlet",
    powerOutlet120VAC: "120V Power Outlet",
    thirdRowClimateControl: "3rd Row Climate Control",
    phoneAsKey: "Phone as Key",
    electricPowertrain: "Electric Powertrain",
    frontTrunk: "Front Trunk (Frunk)",
    headsUpDisplay: "Heads-Up Display",
    multiLevelCargoFloor: "Multi-Level Cargo Floor",
    trailerHitchLight: "Trailer Hitch Light",
    pickupBoxCamera: "Pickup Box Camera",
  };

  // Function to get feature value
  function getFeatureValue(vehicle, feature) {
    return vehicle.maeFeatures?.[feature];
  }

  // Get all unique features from all vehicles
  function getAllFeatures() {
    const featuresSet = new Set();
    sortedTrims.forEach((trim) => {
      if (trim.maeFeatures) {
        Object.keys(trim.maeFeatures).forEach((feature) => {
          featuresSet.add(feature);
        });
      }
    });
    return Array.from(featuresSet);
  }

  // Categorize and organize ALL features
  function organizeFeatures() {
    const allFeatures = getAllFeatures();

    // First, separate by type
    const allBoolean = [];
    const allNumeric = [];
    const allString = [];

    allFeatures.forEach((feature) => {
      const value = getFeatureValue(sortedTrims[0], feature);
      const type = typeof value;

      if (type === "boolean") {
        allBoolean.push(feature);
      } else if (type === "number") {
        allNumeric.push(feature);
      } else if (type === "string") {
        allString.push(feature);
      }
    });

    // Separate each type into common and differing
    function separateByAvailability(features) {
      const common = [];
      const differing = [];

      features.forEach((feature) => {
        const availability = sortedTrims.map((trim) => {
          const value = getFeatureValue(trim, feature);
          return value !== undefined && value !== null && value !== false;
        });

        const allHave = availability.every((has) => has);
        const noneHave = availability.every((has) => !has);

        if (allHave) {
          common.push(feature);
        } else if (!noneHave) {
          differing.push(feature);
        }
      });

      return { common, differing };
    }

    const booleanSeparated = separateByAvailability(allBoolean);
    const numericSeparated = separateByAvailability(allNumeric);
    const stringSeparated = separateByAvailability(allString);

    // Sort differing boolean features for pyramid pattern
    booleanSeparated.differing.sort((a, b) => {
      const aCount = sortedTrims.filter((trim) => {
        const val = getFeatureValue(trim, a);
        return val !== undefined && val !== null && val !== false;
      }).length;

      const bCount = sortedTrims.filter((trim) => {
        const val = getFeatureValue(trim, b);
        return val !== undefined && val !== null && val !== false;
      }).length;

      if (aCount !== bCount) return bCount - aCount;

      for (let i = 0; i < sortedTrims.length; i++) {
        const aHas = getFeatureValue(sortedTrims[i], a);
        const bHas = getFeatureValue(sortedTrims[i], b);
        const aHasFeature =
          aHas !== undefined && aHas !== null && aHas !== false;
        const bHasFeature =
          bHas !== undefined && bHas !== null && bHas !== false;

        if (aHasFeature && !bHasFeature) return -1;
        if (!aHasFeature && bHasFeature) return 1;
      }

      return 0;
    });

    // Return features in order: all boolean (common + differing), then numeric, then string
    return [
      ...booleanSeparated.common,
      ...booleanSeparated.differing,
      ...numericSeparated.common,
      ...numericSeparated.differing,
      ...stringSeparated.common,
      ...stringSeparated.differing,
    ];
  }

  const allFeaturesOrdered = organizeFeatures();

  // Render feature row
  function renderFeatureRow(feature) {
    const displayName = featureDisplayNames[feature] || feature;

    return (
      <React.Fragment key={feature}>
        <div className="chart-container">
          <h3 className="chart-container__label-text">{displayName}</h3>
          {sortedTrims.map((vehicle) => {
            const value = getFeatureValue(vehicle, feature);
            const hasFeature =
              value !== undefined && value !== null && value !== false;

            return (
              <div
                className="chart-container__container"
                key={`${vehicle._id}-${feature}`}
                style={{ display: "flex", flexDirection: "column" }}
              >
                {typeof value === "boolean" ? (
                  hasFeature ? (
                    <CheckIcon check={true} />
                  ) : (
                    <NotAvailableIcon />
                  )
                ) : typeof value === "string" ? (
                  <h3 className="compare-feature-block__text">{value}</h3>
                ) : typeof value === "number" ? (
                  <h3 className="compare-feature-block__text">
                    {Number.isInteger(value) ? value : value.toFixed(1)}
                  </h3>
                ) : (
                  <NotAvailableIcon />
                )}
              </div>
            );
          })}
        </div>
        <hr
          style={{
            border: "none",
            borderTop: "1px solid #e1e1e1",
            margin: "0.3em 0",
            opacity: 0.85,
          }}
        />
      </React.Fragment>
    );
  }

  return (
    <div>
      <div className="as-comparechart" style={{ padding: 5, paddingTop: 0 }}>
        <div className="as-comparechart__container" style={{ gap: "0em" }}>
          {/* Car name */}
          <div className="chart-container">
            <h3 className="chart-container__label-text"></h3>
            {sortedTrims.map((vehicle, index) => (
              <div
                className="chart-container__container"
                key={vehicle._id}
                style={{ display: "flex", flexDirection: "column" }}
              >
                <h3 key={index} className="compare-feature-block__text">
                  {vehicle.raw_year} {vehicle.model} {vehicle.trim}{" "}
                  {`(${vehicle.raw_condition})`}
                </h3>
              </div>
            ))}
          </div>

          <div className="chart-container">
            <h3 className="chart-container__label-text"></h3>
            {sortedTrims.map((vehicle, index) => (
              <div
                className="chart-container__container"
                key={vehicle._id}
                style={{ display: "flex", flexDirection: "column" }}
              >
                <h3 key={index} className="compare-feature-block__text">
                  Available: {vehicle.available}
                </h3>
              </div>
            ))}
          </div>

          {/* Image of the car */}
          <div className="chart-container">
            <p className="chart-container__add_car_sign"></p>
            {sortedTrims.map((vehicle, index) => (
              <div className="chart-container__container" key={index}>
                <div className="img-block">
                  <img
                    className="img-block--img"
                    src={vehicle.raw_imageLink}
                    alt=""
                  />
                </div>
              </div>
            ))}
          </div>

          {/* MSRP */}
          <div className="chart-container" style={{ marginTop: 10 }}>
            <div
              className="label-with-info"
              style={{ justifyContent: "flex-start" }}
            >
              <p className="chart-container__label-text">MSRP</p>
            </div>
            {sortedTrims.map((vehicle, index) => (
              <div key={index} className="price-block">
                <p className="price-block--currency compare-feature-block__text">
                  $
                </p>
                {formatPrice(vehicle["raw_baseMSRP"])}
              </div>
            ))}
          </div>

          <hr
            style={{
              border: "none",
              borderTop: "1px solid #e1e1e1",
              margin: "0.3em 0",
              opacity: 0.85,
            }}
          />

          {/* Drivetrain & Power */}
          <div className="chart-container">
            <div className="compare-feature-block" style={{ gap: 1 }}>
              <p className="chart-container__label-text">Drivetrain & Power</p>
            </div>
            {sortedTrims.map((vehicle, index) => (
              <div
                key={index}
                className="compare-feature-block"
                style={{ gap: 1 }}
              >
                <div className="label-with-info">
                  <div className="compare-feature-block__text">
                    {vehicle.driveTrain} • {vehicle.engine}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <hr
            style={{
              border: "none",
              borderTop: "1px solid #e1e1e1",
              margin: "0.3em 0",
              opacity: 0.85,
            }}
          />

          {/* All Features in Order */}
          {allFeaturesOrdered.map((feature) => renderFeatureRow(feature))}
        </div>
      </div>
    </div>
  );
}
